// @ts-nocheck
import { useState } from 'react';
import { Stack, Group, Button, Menu, ActionIcon, TextInput, Badge } from '@mantine/core';
import {
  IconDownload,
  IconFileTypeCsv,
  IconFileTypeXls,
  IconFilter,
  IconSearch,
  IconX,
} from '@tabler/icons-react';
import DataTable from '../../data/DataTable';
import { Card } from '../../../ui';
import type { DataTableWidgetProps } from '../types';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';

/**
 * DataTableWidget - Enhanced wrapper around DataTable with export and filtering
 *
 * Features:
 * - CSV/Excel export functionality
 * - Quick filter presets
 * - Search functionality
 * - Responsive design
 *
 * @example
 * ```tsx
 * <DataTableWidget
 *   title="User Data"
 *   data={users}
 *   columns={[
 *     { key: 'name', header: 'Name', sortable: true },
 *     { key: 'email', header: 'Email' }
 *   ]}
 *   filterPresets={[
 *     { label: 'Active', filter: (item) => item.status === 'active' },
 *     { label: 'Inactive', filter: (item) => item.status === 'inactive' }
 *   ]}
 * />
 * ```
 */
export function DataTableWidget({
  title,
  subtitle,
  data,
  columns,
  filterPresets = [],
  searchable = true,
  searchKeys = [],
  exportable = true,
  exportFilename,
  onRowClick,
  pagination = true,
  pageSize = 10,
  striped = true,
  highlightOnHover = true,
  ...dataTableProps
}: DataTableWidgetProps) {
  const [activeFilter, setActiveFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  // Apply active filter preset
  let filteredData = data;
  if (activeFilter) {
    const preset = filterPresets.find((p) => p.label === activeFilter);
    if (preset) {
      filteredData = filteredData.filter(preset.filter);
    }
  }

  // Apply search query
  if (searchQuery && searchable && searchKeys.length > 0) {
    const query = searchQuery.toLowerCase();
    filteredData = filteredData.filter((item) =>
      searchKeys.some((key) => {
        const value = item[key];
        return value && String(value).toLowerCase().includes(query);
      })
    );
  }

  // Export to CSV
  const exportToCSV = () => {
    const filename = exportFilename || `${title || 'data'}-${Date.now()}.csv`;

    // Create CSV header
    const headers = columns.map((col) => col.header || col.key).join(',');

    // Create CSV rows
    const rows = filteredData.map((item) =>
      columns.map((col) => {
        const value = item[col.key];
        // Escape commas and quotes
        if (value == null) return '';
        const stringValue = String(value);
        if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
          return `"${stringValue.replace(/"/g, '""')}"`;
        }
        return stringValue;
      }).join(',')
    );

    const csv = [headers, ...rows].join('\n');

    // Download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // Export to Excel (CSV with .xls extension - simple approach)
  const exportToExcel = () => {
    const filename = exportFilename || `${title || 'data'}-${Date.now()}.xls`;

    // Create HTML table
    const headers = columns.map((col) => `<th>${col.header || col.key}</th>`).join('');
    const rows = filteredData.map((item) =>
      `<tr>${columns.map((col) => `<td>${item[col.key] ?? ''}</td>`).join('')}</tr>`
    ).join('');

    const html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
      <head><meta charset="UTF-8"></head>
      <body>
        <table border="1">
          <thead><tr>${headers}</tr></thead>
          <tbody>${rows}</tbody>
        </table>
      </body>
      </html>
    `;

    // Download
    const blob = new Blob([html], { type: 'application/vnd.ms-excel' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  // Clear active filter
  const clearFilter = () => {
    setActiveFilter(null);
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
  };

  return (
    <Card withBorder p="lg">
      <Stack gap="md">
        {/* Header with title and actions */}
        <Group justify="space-between">
          <Stack gap={4}>
            {title && (
              <Group gap="sm">
                <span style={{ fontSize: '1.125rem', fontWeight: 600, ...accessibilityStyles }}>{title}</span>
                {activeFilter && (
                  <Badge
                    size="sm"
                    variant="light"
                    rightSection={
                      <ActionIcon
                        size="xs"
                        color="gray"
                        radius="xl"
                        variant="transparent"
                        onClick={clearFilter}
                      >
                        <IconX size={10} />
                      </ActionIcon>
                    }
                  >
                    {activeFilter}
                  </Badge>
                )}
              </Group>
            )}
            {subtitle && (
              <span style={{ fontSize: '0.875rem', color: 'var(--mantine-color-dimmed)', ...accessibilityStyles }}>
                {subtitle}
              </span>
            )}
          </Stack>

          <Group gap="xs">
            {/* Search */}
            {searchable && searchKeys.length > 0 && (
              <TextInput
                placeholder="Search..."
                leftSection={<IconSearch size={16} />}
                rightSection={
                  searchQuery ? (
                    <ActionIcon size="sm" variant="subtle" onClick={clearSearch}>
                      <IconX size={14} />
                    </ActionIcon>
                  ) : null
                }
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ width: 200 }}
              />
            )}

            {/* Filter Presets */}
            {filterPresets.length > 0 && (
              <Menu shadow="md" width={200}>
                <Menu.Target>
                  <Button
                    variant="default"
                    leftSection={<IconFilter size={16} />}
                    size="sm"
                  >
                    Filter
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Quick Filters</Menu.Label>
                  {filterPresets.map((preset) => (
                    <Menu.Item
                      key={preset.label}
                      onClick={() => setActiveFilter(preset.label)}
                      style={{
                        fontWeight: activeFilter === preset.label ? 600 : 400,
                      }}
                    >
                      {preset.label}
                    </Menu.Item>
                  ))}
                  {activeFilter && (
                    <>
                      <Menu.Divider />
                      <Menu.Item onClick={clearFilter} color="gray">
                        Clear Filter
                      </Menu.Item>
                    </>
                  )}
                </Menu.Dropdown>
              </Menu>
            )}

            {/* Export */}
            {exportable && (
              <Menu shadow="md" width={200}>
                <Menu.Target>
                  <Button
                    variant="default"
                    leftSection={<IconDownload size={16} />}
                    size="sm"
                  >
                    Export
                  </Button>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Export Format</Menu.Label>
                  <Menu.Item
                    leftSection={<IconFileTypeCsv size={16} />}
                    onClick={exportToCSV}
                  >
                    Export as CSV
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconFileTypeXls size={16} />}
                    onClick={exportToExcel}
                  >
                    Export as Excel
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        </Group>

        {/* Data count */}
        <Group gap="xs">
          <span style={{ fontSize: '0.875rem', color: 'var(--mantine-color-dimmed)', ...accessibilityStyles }}>
            Showing {filteredData.length} of {data.length} items
          </span>
        </Group>

        {/* Data Table */}
        <DataTable
          data={filteredData}
          columns={columns}
          onRowClick={onRowClick}
          pagination={pagination}
          pageSize={pageSize}
          striped={striped}
          highlightOnHover={highlightOnHover}
          {...dataTableProps}
        />
      </Stack>
    </Card>
  );
}
